import { Box, TextField, Switch, FormControlLabel, MenuItem, Grid, Typography, Divider, CircularProgress } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { useQuery } from '@tanstack/react-query';
import { tenantSegmentsService } from '../../../../shared/services/tenant/segments';
import { locationService } from '../../../../shared/services/locations/locationService';
import { useEffect, useState } from 'react';

type TenantFormProps = {
  mode: 'create' | 'edit' | 'view';
  loading?: boolean;
  statesOptions?: { id: number; name: string }[];
  citiesOptions?: { id: number; name: string }[];
};

export function TenantForm({
  mode,
  loading = false,
}: TenantFormProps) {
  const { values, errors, touched, handleChange, setFieldValue } = useFormikContext<any>();
  const [selectedStateId, setSelectedStateId] = useState(null);
  
  const isView = mode === 'view';

  const segmentsQuery = useQuery({
    queryKey: ['segments'],
    queryFn: () => tenantSegmentsService.list(),
    enabled: true
  });

  const statesQuery = useQuery({
    queryKey: ['states'],
    queryFn: () => locationService.states(),
    enabled: true
  });

  const citiesQuery = useQuery({
    queryKey: ['cities', selectedStateId],
    queryFn: () => locationService.cities(selectedStateId!),
    enabled: !!selectedStateId,
  });

  useEffect(() => {
    if (touched.stateId) {
      setSelectedStateId(values.stateId);
    }
  }, [values.stateId, touched.stateId]);

  return (
    <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
      
      <Typography variant="h6" fontWeight={700} color="primary.main" mb={1} mt={1}>
        Dados da Empresa
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Nome"
                name="name"
                fullWidth
                required
                disabled={isView || loading}
                error={!!(touched.name && errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Nome Fantasia"
                name="fantasyName"
                fullWidth
                required
                disabled={isView || loading}
                error={!!(touched.fantasyName && errors.fantasyName)}
                helperText={touched.fantasyName && errors.fantasyName}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Razão Social"
                name="corporateName"
                fullWidth
                required
                disabled={isView || loading}
                error={!!(touched.corporateName && errors.corporateName)}
                helperText={touched.corporateName && errors.corporateName}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="CNPJ"
                name="documentNumber"
                fullWidth
                required
                disabled={isView || loading}
                error={!!(touched.documentNumber && errors.documentNumber)}
                helperText={touched.documentNumber && errors.documentNumber}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                select
                required
                label="Segmento"
                name="typeId"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.typeId && errors.typeId)}
                helperText={touched.typeId && errors.typeId}
              >
                {segmentsQuery.data?.data.map((segment: any) => (
                  <MenuItem key={segment.id} value={segment.id}>
                    {segment.name}
                  </MenuItem>
                ))}
                {
                  segmentsQuery.data?.data?.length == 0 ? <MenuItem disabled value="">Nenhuma opção encontrada</MenuItem> : null
                }
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                select
                required
                label="Status"
                name="status"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.status && errors.status)}
                helperText={touched.status && errors.status}
              >
                <MenuItem value="active">Ativo</MenuItem>
                <MenuItem value="inactive">Inativo</MenuItem>
                <MenuItem value="suspended">Suspenso</MenuItem>
                <MenuItem value="blocked">Bloqueado</MenuItem>
              </Field>
            </Grid>
          </Grid>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Inscrição Estadual"
                name="stateRegistration"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.stateRegistration && errors.stateRegistration)}
                helperText={touched.stateRegistration && errors.stateRegistration}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Inscrição Municipal"
                name="municipalRegistration"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.municipalRegistration && errors.municipalRegistration)}
                helperText={touched.municipalRegistration && errors.municipalRegistration}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="E-mail"
                name="email"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.email && errors.email)}
                helperText={touched.email && errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Site"
                name="site"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.site && errors.site)}
                helperText={touched.site && errors.site}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Descrição"
                name="description"
                fullWidth
                multiline
                minRows={2}
                disabled={isView || loading}
                error={!!(touched.description && errors.description)}
                helperText={touched.description && errors.description}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Seção: Dados de Contato */}
      <Typography variant="h6" fontWeight={700} color="primary.main" mb={1} mt={4}>
        Dados de Contato
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="CEP"
                name="zipCode"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.zipCode && errors.zipCode)}
                helperText={touched.zipCode && errors.zipCode}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Distrito"
                name="district"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.district && errors.district)}
                helperText={touched.district && errors.district}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Rua"
                name="street"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.street && errors.street)}
                helperText={touched.street && errors.street}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Nº"
                name="streetNumber"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.streetNumber && errors.streetNumber)}
                helperText={touched.streetNumber && errors.streetNumber}
              />
            </Grid>
          </Grid>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Complemento"
                name="complement"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.complement && errors.complement)}
                helperText={touched.complement && errors.complement}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                label="Telefone"
                name="phoneNumber"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.phoneNumber && errors.phoneNumber)}
                helperText={touched.phoneNumber && errors.phoneNumber}
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                select
                label="Estado"
                name="stateId"
                fullWidth
                disabled={isView || loading}
                error={!!(touched.stateId && errors.stateId)}
                helperText={touched.stateId && errors.stateId}
              >
                {statesQuery.data?.data.map((state: any) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
                {
                  statesQuery.data?.data?.length == 0 ? <MenuItem disabled value="">Nenhuma opção encontrada</MenuItem> : null
                }
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field
                as={TextField}
                select
                label="Cidade"
                name="cityId"
                fullWidth
                disabled={isView || loading || !selectedStateId}
                error={!!(touched.cityId && errors.cityId)}
                helperText={(touched.cityId && errors.cityId) || (selectedStateId ? '' : 'Selecione um estado')}
              >
                {citiesQuery.data?.data.map((city: any) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name}
                  </MenuItem>
                ))}
                {
                  citiesQuery.data?.data?.length == 0 ? <MenuItem disabled value="">Nenhuma opção encontrada</MenuItem> : null
                }
              </Field>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}